import {type ExtendedOptions, type UIChild, type UIElement, UI} from "../../stem-core/ui/UIBase";
import type {StoreClass, StoreObject} from "../../stem-core/state/Store";
import {EndpointPaginator} from "../../stem-core/state/pagination/EndpointPaginator";
import {CollapsiblePanel} from "../../stem-core/ui/collapsible/CollapsiblePanel";
import {SimpleTable} from "../ui/SimpleTable";
import {Button} from "../../stem-core/ui/button/Button";
import {UserDetailsModal} from "./UserDetails";
import {apiMerchantCreateReport, MerchantReport, type MerchantReportType} from "../../client/state/merchant/MerchantReportStore";
import {Toast} from "../../blinkpay/ui/Toast";
import {Messages} from "../../blinkpay/Messages";
import {DashboardPageStyle} from "./theme/DashboardTheme";
import {DashboardTitle} from "./DashboardTitle";
import {merchantService} from "../misc/MerchantService";
import type {Merchant} from "../../client/state/MerchantStore";
import type {MerchantUser} from "../../client/state/merchant/MerchantUserStore";
import {CallThrottler} from "../../stem-core/base/CallModifier.js";
import {Dispatchable} from "../../stem-core/base/Dispatcher";
import {FilterFunnelSVG, GearIcon} from "../SVGElements";
import {CheckboxInput} from "../../stem-core/ui/input/checkbox/CheckboxInput.jsx";
import {makeMetadataColumnFunction, makeMetadataColumnName, AggregateDataFilters,
        type AggregateDataViewManager} from "./AggregateDataViewManager";
import {MerchantReportProgressModal} from "./modals/MerchantReportProgressModal";
import {TopLevelTabArea} from "./theme/TabAreaStyle";
import {BaseEnum, makeEnum} from "../../stem-core/state/BaseEnum.js";
import {DashboardAnchoredPopup} from "../ui/input/PopupInput";
import {ColumnHandler, type ColumnTuple} from "../../stem-core/base/ColumnHandler.js";


class MetadataColumnHandler extends ColumnHandler<StoreObject> {
    // ColumnHandler assigns its options onto the instance, so nothing is initialized here
    declare tablePage?: DashboardTablePage;
    declare filterValue?: any;

    constructor(key: string, headerName: string, value: (entry: StoreObject) => UIChild, tablePage?: DashboardTablePage) {
        super({
            rawHeaderName: headerName,
            headerName: () => this.renderHeader(),
            rawValue: value,
            value: entry => this.renderEntry(entry),
            key,
            tablePage,
        });
    }

    getFilterValue() {
        return this.filterValue;
    }

    setFilterValue(value: any) {
        this.filterValue = value;
        this.tablePage?.applyFilters();  // Triggering the request
    }

    renderHeader() {
        const headerName = this.rawHeaderName;
        const filterValue = this.getFilterValue();
        if (!filterValue) {
            return headerName;
        }
        return [headerName, " ", <FilterFunnelSVG title={"Click to remove filter by " + filterValue}
                                                  size={20} onClick={() => this.setFilterValue(null)}/>];
    }

    renderEntry(entry: StoreObject) {
        const rawValue = this.rawValue(entry);
        const {tablePage} = this;
        if (!rawValue || this.getFilterValue() || !tablePage) {
            return rawValue;
        }

        // TODO Add a filter info on hover, and do this on double-click
        return <div onDoubleClick={() => this.setFilterValue(rawValue)} title={"Double-click to filter by value"}
                    style={{cursor: "help"}}>
            {rawValue}
        </div>
    }
}


export class DashboardTableColumnManager<Row = any> extends Dispatchable {
    metadataColumnMap = new Map<string, MetadataColumnHandler>();

    // All assigned in the constructor
    declare tablePage?: DashboardTablePage;
    declare localStorageKey: string;
    declare columnDefinitions: ColumnTuple<Row>[];
    declare metadataKeys: string[];
    declare selectedColumns: Set<number>;
    declare selectedMetadataColumns: Set<string>;
    declare metadataGetter: (entry: Row, key: string) => any;

    constructor(localStorageKey: string, columnDefinitions: ColumnTuple<Row>[], {metadataGetter = null, tablePage = null}: {
        metadataGetter?: DashboardTableColumnManager<Row>["metadataGetter"];
        tablePage?: DashboardTablePage;
    } = {}) {
        super();
        this.tablePage = tablePage;
        this.localStorageKey = localStorageKey;
        this.columnDefinitions = columnDefinitions;
        this.metadataKeys = [];
        let storedSelection;
        try {
            storedSelection = merchantService.getLocalStorageMap().get(localStorageKey);
        } catch (e) {
            storedSelection = {};
        }
        this.selectedColumns = new Set(storedSelection?.selectedColumns ?? this.columnDefinitions.map((columnDef, index) => index));
        this.selectedMetadataColumns = new Set(storedSelection?.selectedMetadataColumns ?? []);
        // The fallback reads through whatever the row carries, which is why it takes the entry open
        this.metadataGetter = metadataGetter || ((entry: any, key: string) => entry?.conversion?.metadata?.[key]);
    }

    toggleSelectedColumn(index: number) {
        if (this.selectedColumns.has(index)) {
            this.selectedColumns.delete(index);
        } else {
            this.selectedColumns.add(index);
        }
        this.saveSelectionToStorage();
    }

    toggleSelectedMetadataColumn(key: string) {
        if (this.selectedMetadataColumns.has(key)) {
            this.selectedMetadataColumns.delete(key);
        } else {
            this.selectedMetadataColumns.add(key);
        }
        this.saveSelectionToStorage();
    }

    makeMetadataColumn(key: string) {
        // We want to cache them, to preserve filters in the ColumnHandler
        if (!this.metadataColumnMap.has(key)) {
            const metadataColumnFunction = makeMetadataColumnFunction(key);
            this.metadataColumnMap.set(key, new MetadataColumnHandler(
                key,
                makeMetadataColumnName(key),
                // The handler is built for this manager's own table, so the entry is one of its rows
                (entry) => metadataColumnFunction(this.metadataGetter(entry as Row, key)),
                this.tablePage,
            ));
        }
        return this.metadataColumnMap.get(key);
    }

    getMetadataFilters() {
        let filters: Record<string, any> = {};
        for (const [key, columnHandler] of this.metadataColumnMap.entries()) {
            if (columnHandler.filterValue) {
                filters[key] = columnHandler.filterValue;
            }
        }
        return filters;
    }

    getColumns() {
        return [
            ...this.columnDefinitions.filter((columnDef, index) => this.selectedColumns.has(index)),
            ...[...this.selectedMetadataColumns].map(metadataKey => this.makeMetadataColumn(metadataKey)),
        ];
    }

    saveSelectionToStorage() {
        merchantService.getLocalStorageMap().set(this.localStorageKey, {
            selectedColumns: [...this.selectedColumns],
            selectedMetadataColumns: [...this.selectedMetadataColumns],
        })
        this.dispatchChange();
    }
}


export function MakeEmailEntry(merchantUser: MerchantUser) {
    return <div className={DashboardPageStyle.getInstance().userEmail}
                onClick={() => UserDetailsModal.show({merchantUser})}>
        {merchantUser.getEmail()}
    </div>
}


interface TableSettingsInputOptions {
    columnManager?: DashboardTableColumnManager;
}

class TableSettingsInput extends DashboardAnchoredPopup {
    declare options: ExtendedOptions<DashboardAnchoredPopup, TableSettingsInputOptions>;

    render() {
        const {columnManager} = this.options;

        return (
            <div style={{display: "flex", padding: 8}}>
                <div>
                    Columns
                    {columnManager.columnDefinitions.map(([title, entry], index) =>
                        <div>
                            <CheckboxInput initialValue={columnManager.selectedColumns.has(index)} label={title}
                                           onChange={() => columnManager.toggleSelectedColumn(index)}/>
                        </div>
                    )}
                </div>
                {columnManager.metadataKeys.length > 0 && <div style={{paddingLeft: 8}}>
                    Metadata columns
                    {columnManager.metadataKeys.map((key) =>
                        <div>
                            <CheckboxInput initialValue={columnManager.selectedMetadataColumns.has(key)}
                                           onChange={() => columnManager.toggleSelectedMetadataColumn(key)}
                                           label={makeMetadataColumnName(key)}/>
                        </div>
                    )}
                </div>}
            </div>
        )
    }
}

interface ReportTableOptions<Row = any> {
    columnManager?: DashboardTableColumnManager<Row>;
    exportCallback?: () => void;
}

class ReportTable<Row = any> extends SimpleTable<Row, ReportTableOptions<Row>> {
    requestTableSettings(anchor: UIElement) {
        const {columnManager} = this.options;
        TableSettingsInput.show({columnManager, anchor});
    }

    getHeaderAction() {
        const {exportCallback, columnManager} = this.options;

        return [
            columnManager && <GearIcon
                size={20}
                style={{cursor: "pointer"}}
                onClick={(event, element) => this.requestTableSettings(element)}
            />,
            exportCallback && [
                <Button onClick={exportCallback}>Export to CSV</Button>,
                <span style={{verticalAlign: "center"}}>Export all filtered data.</span>
            ]
        ];
    }
}


// This should be a multiple inheritance from TabArea and DashboardBaseInput, but for now it's quite simple
interface FlatTabInputOptions {
    options?: any[];
    onChange?: (...args: any[]) => any;
}

export class FlatTabInput extends TopLevelTabArea {
    // TopLevelTabArea fixes its own options type and doesn't forward a type argument
    declare options: ExtendedOptions<TopLevelTabArea, FlatTabInputOptions>;

    // Set by onSetActive, read by getValue - no initializer to emit
    declare value?: any;

    render() {
        const {options} = this.options;
        const currentValue = this.getValue();
        return options.map(option => <div title={option} active={option === currentValue} value={option}/>);
    }

    // The panels are the <div value={option}/> the switcher renders above
    onSetActive(panel: UIElement<{value?: any}>) {
        super.onSetActive(panel);
        const newValue = panel.options.value;
        if (newValue !== this.getValue()) {
            this.value = newValue;
            this.dispatch("change", newValue, this);
        }
    }

    getValue() {
        return this.value || this.options.options[0];
    }
}


@makeEnum
class DashboardTablePageMode extends BaseEnum {
    static INDIVIDUAL;
    static AGGREGATE;
}


// Row is what the page lists: the store it pages through, and what its columns read
interface DashboardTablePageOptions<Row extends StoreObject = StoreObject> {
    merchant?: Merchant;
    store?: StoreClass<Row>;
    endpoint?: string;
    paginatorOptions?: Record<string, any>;
    title?: UIChild;
    description?: UIChild;
    reportType?: MerchantReportType;
    columnManager?: DashboardTableColumnManager<Row>;
    exportCallback?: () => void;
}

export class DashboardTablePage<Row extends StoreObject = StoreObject, ExtraOptions = {}> extends UI.Element<DashboardTablePageOptions<Row> & ExtraOptions> {
    // Subclasses assign these; the base only ever reads them
    declare viewModeInput?: FlatTabInput;
    declare paginator?: EndpointPaginator<Row>;

    filtersThrottler = new CallThrottler({debounce: 200});
    applyFilters = this.filtersThrottler.wrap(() => {
        const filters = this.getFilters();

        this.getPaginator().updateFilter(filters);
    });
    groupsManager: AggregateDataViewManager | null = null;
    columnManager: DashboardTableColumnManager<Row> | null = null;

    getMerchant() {
        return this.options.merchant;
    }

    getCurrency() {
        return this.getMerchant().getCurrency();
    }

    getViewMode() {
        return this.viewModeInput?.getValue() || DashboardTablePageMode.INDIVIDUAL;
    }

    isInAggregateView() {
        return this.getViewMode() === DashboardTablePageMode.AGGREGATE;
    }

    // The entries paginator is created whichever view is up, the aggregate one does not replace it
    getEntriesPaginator() {
        const {store, endpoint, paginatorOptions} = this.options;
        this.paginator = this.paginator || new EndpointPaginator(store, endpoint, {
            ...paginatorOptions,
        });
        return this.paginator;
    }

    getAggregatePaginator() {
        const {endpoint, paginatorOptions} = this.options;
        return this.groupsManager.getPaginator(endpoint, paginatorOptions);
    }

    getPaginator() {
        const entriesPaginator = this.getEntriesPaginator();
        return this.isInAggregateView() ? this.getAggregatePaginator() : entriesPaginator;
    }

    getTitleSection(): UIChild {
        const {title, description} = this.options;
        return title && <DashboardTitle title={title} description={description}/>
    }

    getFilterSection(): UIChild;
    getFilterSection(): any {
    }

    // If the paginator for the current view doesn't have a last response, fall back
    // to the one in the default paginator.
    getSummaryLastResponse() {
        return this.getPaginator().getLastResponse() || this.paginator.getLastResponse();
    }

    getFilters() {
        return {};
    }

    summaryContent(summaryLastResponse: Record<string, any>): UIChild;
    summaryContent(summaryLastResponse: Record<string, any>): any {
    }

    summarySection(): UIChild {
        const summaryLastResponse = this.getSummaryLastResponse();
        if (!summaryLastResponse) {
            return;
        }
        const content = this.summaryContent(summaryLastResponse);
        if (!content) {
            return null;
        }
        return <CollapsiblePanel
            collapsed={false}
            style={{marginTop: 20}}
            title="Summary & Statistics">
            {content}
        </CollapsiblePanel>
    }

    getColumns() {
        return this.columnManager?.getColumns() || [];
    }

    async generateReport() {
        const {merchant, reportType} = this.options;
        const filters = this.getFilters();
        try {
            const response = await apiMerchantCreateReport(merchant.id, reportType, filters);
            const merchantReport = MerchantReport.loadObject(response);
            if (merchantReport) {
                MerchantReportProgressModal.show({merchantReport});
            } else {
                throw "Unknown error in generating report";
            }

        } catch (error) {
            Toast.showError(error || Messages.reportNotGenerated);
        }
    }

    // The two views list different things, so each table gets the paginator for what it lists
    entriesTable() {
        const entriesPaginator = this.getEntriesPaginator();

        if (this.isInAggregateView()) {
            return [
                <AggregateDataFilters groupsManager={this.groupsManager}/>,
                <ReportTable
                    paginator={this.getAggregatePaginator()}
                    noAutofetch={true}
                    columns={this.groupsManager.getTableColumns()}
                />
            ]
        }

        return (
            <ReportTable<Row>
                paginator={entriesPaginator}
                noAutofetch={true}
                columnManager={this.columnManager}
                exportCallback={this.options.reportType && (() => this.generateReport())}
                columns={this.getColumns()}
            />
        );
    }

    render() {
        return [
            this.getTitleSection(),
            this.getFilterSection(),
            this.summarySection(),
            this.groupsManager && <FlatTabInput
                ref="viewModeInput"
                options={DashboardTablePageMode.all()}
                onChange={(value: DashboardTablePageMode) => this.redrawAndApplyFilters()}
            />,
            this.entriesTable(),
        ];
    }

    redrawAndApplyFilters() {
        this.redraw();
        this.applyFilters();
    }

    onMount() {
        // For the first page load, also load the metadata keys.
        this.attachListenerOnce(this.getPaginator(), "pageLoaded", (results: StoreObject[], response: {allMetadataKeys?: string[]}) => {
            if (response && response.allMetadataKeys) {
                if (this.columnManager) {
                    this.columnManager.metadataKeys = response.allMetadataKeys;
                }
                if (this.groupsManager) {
                    this.groupsManager.addMetadataKeys(response.allMetadataKeys);
                }
            }
        });
        this.attachListener(this.paginator, "pageLoaded", () => this.redraw());
        this.applyFilters();
        if (this.columnManager) {
            this.attachChangeListener(this.columnManager, () => this.redraw());
        }
        if (this.groupsManager) {
            this.attachChangeListener(this.groupsManager, () => this.redraw());
        }
    }
}
